import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { County } from '../../models/county.model';
import { CountyService } from '../../services/county.service';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-county-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    InputText,
    Select,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './county-management.component.html',
  styleUrl: './county-management.component.scss',
  providers: [ConfirmationService]
})
export class CountyManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  counties: County[] = [];
  filteredCounties: County[] = [];
  selectedCounty: County = this.getEmptyCounty();
  fieldTouched: Record<string, boolean> = {};
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';

  rows: number = 50;
  first: number = 0;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalCountiesCount(): number { return this.counties.length; }
  get activeCountiesCount(): number { return this.counties.filter(c => c.isActive).length; }
  get inactiveCountiesCount(): number { return this.counties.filter(c => !c.isActive).length; }

  constructor(
    private countyService: CountyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by country name...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Country');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadCounties();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.exportToExcel());
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!user?.isAdmin);
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadCounties() {
    this.loading = true;
    this.countyService.getAllCounties().pipe(
      catchError(error => {
        console.error('Error loading counties:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load countries. Please refresh the page or try again later.'
        });
        this.loading = false;
        return of([] as County[]);
      })
    ).subscribe({
      next: (data) => {
        this.counties = data.map(c => ({
          ...c,
          isActive: c.isActive !== undefined ? c.isActive : true
        }));
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.counties];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(c => c.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(c => c.isActive === false);
      }
    }

    this.filteredCounties = filtered;
    this.totalRecords = filtered.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredCounties.length === 0);
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() || (this.selectedStatusFilter != null && this.selectedStatusFilter !== 'active'));
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.applySearch();
  }

  showAddDialog() {
    this.selectedCounty = this.getEmptyCounty();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(county: County) {
    this.selectedCounty = { ...county };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  saveCounty() {
    this.fieldTouched['name'] = true;
    if (!this.selectedCounty.name.trim()) {
      return;
    }

    this.loading = true;

    if (this.dialogMode === 'add') {
      this.countyService.createCounty(this.selectedCounty).pipe(
        catchError(error => {
          console.error('Error creating county:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create county. Please try again.'
          });
          this.loading = false;
          return of(null);
        })
      ).subscribe({
        next: (result) => {
          if (result) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'County created successfully.'
            });
            this.hideDialog();
            this.loadCounties();
          }
        }
      });
    } else {
      this.countyService.updateCounty(this.selectedCounty.id!, this.selectedCounty).pipe(
        catchError(error => {
          console.error('Error updating county:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update county. Please try again.'
          });
          this.loading = false;
          return of(null);
        })
      ).subscribe({
        next: (result) => {
          if (result) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'County updated successfully.'
            });
            this.hideDialog();
            this.loadCounties();
          }
        }
      });
    }
  }

  toggleActiveStatus(county: County) {
    const activating = !county.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${county.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        county.isActive = !county.isActive;
        this.updateCountyStatus(county.id!, county);
      }
    });
  }

  private updateCountyStatus(id: string, county: County) {
    this.loading = true;
    this.countyService.updateCounty(id, county).pipe(
      catchError(error => {
        console.error('Error updating county status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update county status. Please try again.'
        });
        this.loading = false;
        return of(null);
      })
    ).subscribe({
      next: (result) => {
        if (result) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'County status updated successfully.'
          });
          this.loadCounties();
        }
      }
    });
  }

  deleteCounty(county: County) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${county.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.loading = true;
        this.countyService.deleteCounty(county.id!).pipe(
          catchError(error => {
            console.error('Error deleting county:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete county. Please try again.'
            });
            this.loading = false;
            return of(null);
          })
        ).subscribe({
          next: (result) => {
            if (result !== null) {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'County deleted successfully.'
              });
              this.loadCounties();
            }
          }
        });
      }
    });
  }

  hideDialog() {
    this.displayDialog = false;
    this.selectedCounty = this.getEmptyCounty();
    this.fieldTouched = {};
  }

  resetCountyForm() {
    this.selectedCounty = this.getEmptyCounty();
    this.fieldTouched = {};
  }

  exportToExcel(): void {
    if (!this.filteredCounties?.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No countries to export' });
      return;
    }
    try {
      const exportData = this.filteredCounties.map(c => ({
        'Name': c.name || '',
        'Status': c.isActive ? 'Active' : 'Inactive'
      }));
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [{ wch: 28 }, { wch: 10 }];
      const workbook: XLSX.WorkBook = { Sheets: { 'Countries': worksheet }, SheetNames: ['Countries'] };
      const excelBuffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `Countries_${this.formatDateForFile(new Date())}.xlsx`);
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Countries exported to Excel successfully' });
    } catch (error) {
      console.error('Error exporting countries:', error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export countries to Excel' });
    }
  }

  private formatDateForFile(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  getEmptyCounty(): County {
    return {
      name: '',
      isActive: true
    };
  }
}